import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '@/components/feature/SuperAdminLayout';
import SAHeader from '@/components/feature/SAHeader';
import LoadingState from '@/components/base/LoadingState';
import { requireSuperAdmin } from '@/middleware/superAdminGuard';
import PlansTab from './components/PlansTab';
import GlobalSettingsTab from './components/GlobalSettingsTab';

const TABS = [
  { id: 'system',   label: 'System Info',     icon: 'ri-server-line' },
  { id: 'plans',    label: 'Plans & Limits',  icon: 'ri-price-tag-3-line' },
  { id: 'storage',  label: 'Storage',         icon: 'ri-hard-drive-line' },
  { id: 'security', label: 'Security',        icon: 'ri-shield-check-line' },
  { id: 'global',   label: 'Global Settings', icon: 'ri-settings-4-line' },
] as const;

type TabId = (typeof TABS)[number]['id'];

const SYSTEM_INFO = [
  { label: 'Plataforma',    value: 'Pixory AI SaaS' },
  { label: 'Versión',       value: 'v1.0.0-beta' },
  { label: 'Entorno',       value: 'Production' },
  { label: 'Base de datos', value: 'Supabase PostgreSQL' },
  { label: 'Auth',          value: 'Supabase Auth' },
  { label: 'Frontend',      value: 'React 19 + Vite + TailwindCSS' },
  { label: 'Edge Functions',value: 'Supabase Deno' },
];

const SECURITY_ITEMS = [
  { label: 'Row Level Security (RLS)',       status: 'Activo',     ok: true },
  { label: 'Super Admin aislado del tenant', status: 'Activo',     ok: true },
  { label: 'Auth via Supabase',              status: 'Activo',     ok: true },
  { label: 'Acceso Super Admin por tabla',   status: 'Verificado', ok: true },
  { label: 'HTTPS / SSL',                    status: 'Via host',   ok: true },
  { label: 'API keys en .env',               status: 'Aplicado',   ok: true },
];

export default function SASettings() {
  const navigate   = useNavigate();
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>('system');

  useEffect(() => {
    const init = async () => {
      const ok = await requireSuperAdmin();
      if (!ok) { navigate('/login'); return; }
      setLoading(false);
    };
    init();
  }, [navigate]);

  if (loading) return <SuperAdminLayout><SAHeader title="Settings" subtitle="Configuración de la plataforma" /><LoadingState /></SuperAdminLayout>;

  return (
    <SuperAdminLayout>
      <SAHeader title="Settings" subtitle="Configuración y límites de la plataforma Pixory AI" />
      <div className="px-4 md:px-8 py-4 md:py-7 bg-[#f3f7fb] min-h-[calc(100vh-64px)]">

        {/* Tab Bar */}
        <div className="flex items-center gap-1 border-b border-slate-200 mb-7 overflow-x-auto">
          {TABS.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all whitespace-nowrap cursor-pointer border-b-2 -mb-px ${
                activeTab === tab.id ? 'text-[#2563eb] border-[#2563eb]' : 'text-slate-500 border-transparent hover:text-slate-700 hover:border-slate-300'
              }`}>
              <span className="w-4 h-4 flex items-center justify-center"><i className={`${tab.icon} text-sm`}></i></span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* System Info */}
        {activeTab === 'system' && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {SYSTEM_INFO.map((info) => (
                <div key={info.label} className="bg-white rounded-xl p-4 border border-slate-200">
                  <p className="text-slate-500 text-xs mb-1">{info.label}</p>
                  <p className="text-slate-900 text-sm font-semibold">{info.value}</p>
                </div>
              ))}
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-5 h-5 flex items-center justify-center text-[#2563eb]"><i className="ri-stack-line text-sm"></i></span>
                <h4 className="text-slate-700 text-sm font-semibold">Arquitectura del sistema</h4>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { layer: 'Frontend', tech: 'React + Vite', role: 'SPA — UI, routing, state', icon: 'ri-layout-2-line', color: 'text-sky-600 bg-sky-50' },
                  { layer: 'Backend',  tech: 'Supabase',     role: 'Auth, DB, Edge Functions, RLS', icon: 'ri-database-2-line', color: 'text-emerald-600 bg-emerald-50' },
                  { layer: 'Storage',  tech: 'VPS',          role: 'Fotos, videos, ZIPs — /storage/YYYY/MM/', icon: 'ri-hard-drive-line', color: 'text-amber-600 bg-amber-50' },
                ].map((l) => (
                  <div key={l.layer} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <span className={`w-8 h-8 flex items-center justify-center rounded-lg mb-3 ${l.color}`}><i className={`${l.icon} text-sm`}></i></span>
                    <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-0.5">{l.layer}</p>
                    <p className="text-slate-900 text-sm font-semibold">{l.tech}</p>
                    <p className="text-slate-500 text-xs mt-1">{l.role}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Plans & Limits */}
        {activeTab === 'plans' && <PlansTab />}

        {/* Storage */}
        {activeTab === 'storage' && (
          <div className="space-y-5 max-w-3xl">
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-50 text-amber-600 shrink-0"><i className="ri-hard-drive-line text-lg"></i></span>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-slate-900 text-sm font-semibold">VPS Storage</h4>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Backend Pending</span>
                  </div>
                  <p className="text-slate-500 text-sm leading-relaxed">Supabase almacena únicamente registros de DB, auth, metadata y URLs públicas. Los archivos pesados (fotos, videos, ZIPs, álbumes) residen en el VPS bajo <span className="font-mono text-slate-700">/storage/YYYY/MM/</span>.</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { title: 'Supabase', color: 'text-emerald-600 bg-emerald-50', border: 'border-emerald-200', icon: 'ri-database-2-line',
                  items: ['Auth tokens & sessions', 'Tenant / user records', 'Contracts & contracts text', 'Job metadata', 'File URLs (public links)', 'Settings & config', 'Activity logs & notifications'] },
                { title: 'VPS', color: 'text-amber-600 bg-amber-50', border: 'border-amber-200', icon: 'ri-hard-drive-line',
                  items: ['Job photos (JPG/RAW)', 'Delivery ZIPs', 'Online albums', 'Invitation media', 'Branding logos', 'Gallery images', 'Media package files'] },
              ].map((s) => (
                <div key={s.title} className={`bg-white border ${s.border} rounded-xl p-5`}>
                  <div className="flex items-center gap-2 mb-4">
                    <span className={`w-7 h-7 flex items-center justify-center rounded-lg ${s.color}`}><i className={`${s.icon} text-sm`}></i></span>
                    <h4 className={`text-sm font-semibold ${s.color.split(' ')[0]}`}>{s.title}</h4>
                  </div>
                  <ul className="space-y-2">
                    {s.items.map((item) => (
                      <li key={item} className="flex items-center gap-2 text-xs text-slate-500">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${s.color.includes('emerald') ? 'bg-emerald-300' : 'bg-amber-300'}`}></span>{item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100"><p className="text-slate-500 text-xs uppercase tracking-wider font-medium">Estado del backend VPS</p></div>
              {['upload.php — recibe archivos del frontend', 'delete.php — elimina archivos del VPS', 'STORAGE_CONFIG.enabled en storageConfig.ts', 'URL pública configurada'].map((item) => (
                <div key={item} className="flex items-center gap-3 px-5 py-3 border-b border-slate-100 last:border-0">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-amber-50 text-amber-600 shrink-0"><i className="ri-time-line text-xs"></i></span>
                  <p className="text-slate-600 text-sm flex-1">{item}</p>
                  <span className="text-xs font-semibold text-amber-600 whitespace-nowrap">Backend Pending</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Security */}
        {activeTab === 'security' && (
          <div className="space-y-5 max-w-2xl">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100"><p className="text-slate-500 text-xs uppercase tracking-wider font-medium">Estado de seguridad</p></div>
              {SECURITY_ITEMS.map((item) => (
                <div key={item.label} className="flex items-center gap-3 px-5 py-3.5 border-b border-slate-100 last:border-0">
                  <span className={`w-7 h-7 flex items-center justify-center rounded-full shrink-0 ${item.ok ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`}>
                    <i className={`text-xs ${item.ok ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'}`}></i>
                  </span>
                  <p className="text-slate-700 text-sm flex-1">{item.label}</p>
                  <span className={`text-xs font-semibold whitespace-nowrap ${item.ok ? 'text-emerald-600' : 'text-red-500'}`}>{item.status}</span>
                </div>
              ))}
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h4 className="text-slate-500 text-xs uppercase tracking-wider font-medium mb-4">Principios aplicados</h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: 'ri-lock-line',       title: 'Zero Trust',        desc: 'RLS en todas las tablas. Nunca confiar en el cliente.' },
                  { icon: 'ri-eye-off-line',     title: 'Secrets en .env',   desc: 'Keys fuera del código fuente. Nunca expuestas en el repo.' },
                  { icon: 'ri-team-line',        title: 'Tenant isolation',  desc: 'Cada tenant solo ve sus datos via RLS.' },
                  { icon: 'ri-shield-star-line', title: 'SA separation',     desc: 'Super Admin tiene tabla dedicada, no es un tenant.' },
                ].map((p) => (
                  <div key={p.title} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="w-5 h-5 flex items-center justify-center text-[#2563eb]"><i className={`${p.icon} text-xs`}></i></span>
                      <p className="text-slate-700 text-xs font-semibold">{p.title}</p>
                    </div>
                    <p className="text-slate-500 text-xs leading-relaxed">{p.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Global Settings */}
        {activeTab === 'global' && <GlobalSettingsTab />}
      </div>
    </SuperAdminLayout>
  );
}
